// ===========================================
// EventPixabayPicker
// ===========================================
// Reusable Pixabay search + image-grid component for events.
// Used by:
//   - EventPhotosPanel  (gallery flow — needs caption + eventId)
//   - EventEditorPage   (featured-image flow — onSelect(url) callback)
//
// When `onSelect` is provided the component operates in "feature image" mode:
// it calls selectPixabayFeatureImage, downloads + re-hosts the image, and
// returns the final URL via the callback — no caption field, no eventId needed.
//
// When `onSelect` is NOT provided the component falls back to the original
// gallery behaviour (requires eventId + addPhotoMutation).

import { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  InputAdornment,
  Stack,
  Chip,
} from '@mui/material';
import {
  Search as SearchIcon,
  CheckCircle as CheckIcon,
  AddPhotoAlternate as AddPhotoIcon,
} from '@mui/icons-material';
import { usePixabaySearch } from '../../hooks/useEvents';
import { PixabaySearchResult } from '../../api/events.api';
import { eventApi } from '../../api/events.api';
import { useSnackbar } from 'notistack';

// ===========================================
// CONSTANTS
// ===========================================

const SUGGESTED_CATEGORIES = [
  'music',
  'concert',
  'recital',
  'piano',
  'guitar',
  'violin',
  'performance',
  'children',
];

// ===========================================
// PROPS
// ===========================================

interface EventPixabayPickerProps {
  /** Feature-image mode: called with the re-hosted URL on pick. */
  onSelect?: (url: string) => void;
  /** Gallery mode: used by EventPhotosPanel to add a photo to an event. */
  onAddToGallery?: (image: PixabaySearchResult, caption: string) => Promise<void>;
  /** Whether the gallery add action is in progress (gallery mode only). */
  isAdding?: boolean;
}

// ===========================================
// COMPONENT
// ===========================================

export default function EventPixabayPicker({
  onSelect,
  onAddToGallery,
  isAdding = false,
}: EventPixabayPickerProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedImage, setSelectedImage] = useState<PixabaySearchResult | null>(null);
  const [caption, setCaption] = useState('');
  const [isSelectingFeature, setIsSelectingFeature] = useState(false);

  const searchMutation = usePixabaySearch();
  const results: PixabaySearchResult[] = searchMutation.data ?? [];

  const isFeatureMode = !!onSelect;

  // NOTE: deliberately NOT wrapped in a <form>. This picker is rendered inside
  // the Event editor's <form>, and a nested form is invalid HTML and would let
  // an Enter keypress submit the OUTER form (saving the event). Search is driven
  // by the button onClick + an Enter keydown on the field instead.
  const triggerSearch = () => {
    if (searchQuery.trim()) {
      searchMutation.mutate({ q: searchQuery.trim() });
    }
  };

  const handleCategoryClick = (category: string) => {
    setSearchQuery(category);
    searchMutation.mutate({ q: category });
  };

  const handleAction = async () => {
    if (!selectedImage) return;

    if (isFeatureMode) {
      // Feature image mode: download + re-host, then return URL
      setIsSelectingFeature(true);
      try {
        const result = await eventApi.selectPixabayFeatureImage({
          imageUrl: selectedImage.fullUrl,
          pixabayPageUrl: selectedImage.pageUrl,
          user: selectedImage.user,
        });
        onSelect!(result.url);
        setSelectedImage(null);
      } catch {
        enqueueSnackbar('Failed to select image. Please try again.', { variant: 'error' });
      } finally {
        setIsSelectingFeature(false);
      }
    } else if (onAddToGallery) {
      // Gallery mode: delegate to parent
      await onAddToGallery(selectedImage, caption.trim());
      setSelectedImage(null);
      setCaption('');
    }
  };

  const actionPending = isFeatureMode ? isSelectingFeature : isAdding;
  const actionLabel = isFeatureMode ? 'Use Image' : 'Add Photo';

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
        {isFeatureMode ? 'Choose from Pixabay' : 'Add from Pixabay'}
      </Typography>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <TextField
          fullWidth
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              triggerSearch();
            }
          }}
          placeholder="Search free stock photos..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Button
          type="button"
          onClick={triggerSearch}
          variant="contained"
          disableElevation
          disabled={searchMutation.isPending}
          sx={{ flexShrink: 0, minWidth: 90 }}
        >
          {searchMutation.isPending ? <CircularProgress size={18} /> : 'Search'}
        </Button>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 0.5 }}>
        {SUGGESTED_CATEGORIES.map((cat) => (
          <Chip
            key={cat}
            label={cat}
            size="small"
            onClick={() => handleCategoryClick(cat)}
            variant="outlined"
          />
        ))}
      </Stack>

      {searchMutation.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Search failed. Please try again.
        </Alert>
      )}

      {results.length > 0 && (
        <>
          <ImageList cols={4} gap={8} sx={{ maxHeight: 320, mb: 2 }}>
            {results.map((img) => (
              <ImageListItem
                key={img.pixabayId}
                onClick={() => setSelectedImage(img)}
                sx={{
                  cursor: 'pointer',
                  border:
                    selectedImage?.pixabayId === img.pixabayId
                      ? '3px solid'
                      : '3px solid transparent',
                  borderColor:
                    selectedImage?.pixabayId === img.pixabayId
                      ? 'primary.main'
                      : 'transparent',
                  borderRadius: 1,
                  overflow: 'hidden',
                  '&:hover': { opacity: 0.85 },
                }}
              >
                <img
                  src={img.previewUrl}
                  alt={img.tags}
                  loading="lazy"
                  style={{ height: 90, objectFit: 'cover', width: '100%' }}
                />
                {selectedImage?.pixabayId === img.pixabayId && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      bgcolor: 'primary.main',
                      borderRadius: '50%',
                      p: 0.25,
                      lineHeight: 0,
                    }}
                  >
                    <CheckIcon sx={{ fontSize: 16, color: 'white' }} />
                  </Box>
                )}
                <ImageListItemBar
                  subtitle={`by ${img.user}`}
                  sx={{ '& .MuiImageListItemBar-subtitle': { fontSize: '0.7rem' } }}
                />
              </ImageListItem>
            ))}
          </ImageList>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Images provided by Pixabay — free for commercial use
          </Typography>

          {selectedImage && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
              {!isFeatureMode && (
                <TextField
                  label="Caption (optional)"
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  size="small"
                  sx={{ flex: 1 }}
                />
              )}
              <Button
                variant="contained"
                startIcon={
                  actionPending ? (
                    <CircularProgress size={14} />
                  ) : (
                    <AddPhotoIcon />
                  )
                }
                onClick={handleAction}
                disabled={actionPending}
              >
                {actionLabel}
              </Button>
            </Stack>
          )}
        </>
      )}

      {!searchMutation.isPending && results.length === 0 && searchMutation.isSuccess && (
        <Typography color="text.secondary" variant="body2">
          No images found. Try a different search term.
        </Typography>
      )}
    </Box>
  );
}
